import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { es } from 'date-fns/locale';
import { Control, Controller, FieldErrors } from 'react-hook-form';

import Button from '@/components/Button';
import useResponsive from '@/hooks/useResponsive';
import { IWarehouse } from '@/types/fulfill/warehouse/warehouse.interface';

import { SendToWarehouseFormData } from '../../inventorySchema';

interface WhentToSendProps {
  step: number;
  setCurrentStep: (step: number) => void;
  errors: FieldErrors<SendToWarehouseFormData>;
  control: Control<SendToWarehouseFormData>;
  editStep: boolean;
  isDisabled: boolean;
  selectedWarehouse?: IWarehouse;
  requiresHomePickup?: boolean;
}

const WhenToSend = ({
  step,
  setCurrentStep,
  errors,
  control,
  editStep,
  isDisabled,
  selectedWarehouse,
  requiresHomePickup,
}: WhentToSendProps) => {
  const theme = useTheme();
  const isSmallDrawer = useResponsive('between', 'lg', 'xl');
  const isPhone = useResponsive('down', 'sm');
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          margin: '1rem 0',
          [theme.breakpoints.between('lg', 'xl')]: {
            flexDirection: 'column',
            gap: '1rem',
          },
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: '1rem',
          },
        }}
      >
        <Typography variant="subtitle1" sx={{ minWidth: '4.1rem' }}>
          Paso <span style={{ color: theme.palette.primary.main }}>2</span>
        </Typography>
        <Divider
          orientation={isSmallDrawer || isPhone ? 'horizontal' : 'vertical'}
          flexItem
          sx={{
            margin: isSmallDrawer || isPhone ? '0' : `0 ${theme.spacing(2)}`,
          }}
        />
        <Box>
          <Typography variant="subtitle2">Elige cuándo enviar tus productos</Typography>
          <br />
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{
              '&& span': {
                color: theme.palette.primary.main,
              },
            }}
          >
            <span>Reserva una fecha</span> que te quede cómoda para enviarnos tus productos
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          padding: `${theme.spacing(3)}`,
        }}
      >
        <Typography variant="body1">
          <strong>Estás por finalizar</strong> tu proceso de envío.
        </Typography>{' '}
        <br />
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.grey[500],
            '&& span': { color: theme.palette.primary.main },
          }}
        >
          Recibiremos tus productos en:{' '}
          <span>{selectedWarehouse?.name || 'Información no disponible'}</span>
        </Typography>
        <br />
        <Typography variant="body2" color={theme.palette.grey[500]}>
          {selectedWarehouse?.location.address || 'Información no disponible'}
        </Typography>
        <br />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <Controller
            name="appointment"
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                disablePast
                label="Fecha"
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    error: !!errors.appointment,
                    helperText: errors.appointment ? errors.appointment.message : '',
                  },
                  popper: {
                    placement: 'bottom-start',
                    disablePortal: true,
                  },
                }}
              />
            )}
          />
        </LocalizationProvider>
        <Box sx={{ marginTop: theme.spacing(3) }}>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.grey[500],
              marginBottom: theme.spacing(1),
            }}
          >
            Seleccione si requiere recolección a domicilio
          </Typography>
          <Controller
            name="requiresHomePickup"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value || false} color="primary" />}
                label={
                  <Typography variant="body1" sx={{ color: theme.palette.secondary.main }}>
                    Recolección a domicilio
                  </Typography>
                }
              />
            )}
          />
        </Box>
      </Paper>

      <Grid
        container
        size={{ xxs: 12 }}
        mt={4}
        sx={{
          display: 'flex',
          flexDirection: { xxs: 'column-reverse', sm: 'row', lg: 'column-reverse', xl: 'row' },
          justifyContent: { xxs: 'center', sm: 'space-between', lg: 'center', xl: 'space-between' },
          width: '100%',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <Button variant="outlined" onClick={() => setCurrentStep(step - 1)}>
          Atrás
        </Button>
        <Button
          disabled={isDisabled}
          onClick={() =>
            editStep ? setCurrentStep(requiresHomePickup ? 5 : 4) : setCurrentStep(step + 1)
          }
        >
          Continuar
        </Button>
      </Grid>
    </Box>
  );
};

export default WhenToSend;
